import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Dialog from '../../components/Dialog';
import '../../styles/admin/AdminRefundDetail.css';
import { getRefundDetail, rejectRefundRequest, approveRefundRequest } from '../../utils/api';

const AdminRefundDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [refundData, setRefundData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');

  useEffect(() => {
    // API를 호출하여 환불 요청 데이터를 가져옵니다
    const fetchRefundData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('환불 요청 상세 정보 조회 ID:', id);
        const response = await getRefundDetail(id);
        console.log('환불 요청 상세 정보 응답:', response);
        
        if (response && response.status === 'success' && response.data) {
          const data = response.data;
          
          // 중첩된 객체 구조 접근
          const refundData = data.refund || {};
          const companyData = data.company || {};
          const paymentData = data.payment || {};
          const refundInfo = paymentData.refund_info || {};
          const cardInfo = paymentData.card || {};
          
          // 날짜 포맷 변환
          const paymentDateStr = paymentData.payment_date || '';
          const purchaseDate = paymentDateStr || '날짜 정보 없음';
          
          // 환불 요청 날짜 포맷
          let requestDate = '날짜 정보 없음';
          if (refundData.request_date) {
            const requestDateObj = new Date(refundData.request_date);
            requestDate = `${requestDateObj.getFullYear()}년 ${requestDateObj.getMonth() + 1}월 ${requestDateObj.getDate()}일 ${requestDateObj.getHours()}:${requestDateObj.getMinutes()}`;
          }
          
          // 환불 상태 한글 변환
          const statusMap = {
            'requested': '요청됨',
            'approved': '승인됨',
            'rejected': '거절됨',
            'processing': '처리중',
            'completed': '완료됨'
          };
          const statusKorean = statusMap[refundData.status] || '알 수 없음';
          
          // 플랜 기간 포맷
          const planPeriod = paymentData.period || '미상';
          
          // 결제 데이터 설정
          const formattedRefundData = {
            id: refundData.id || id,
            companyName: companyData.company_name || '업체명 없음',
            phone: companyData.phone_number || '연락처 없음',
            companyCode: companyData.company_code || refundData.company_code || '',
            transactionId: refundData.payment_id || '',
            paymentMethod: paymentData.payment_method || '결제 방법 없음',
            purchaseDate: purchaseDate,
            plan: paymentData.plan_name || 'TapGo Plus 플랜',
            planPeriod: planPeriod,
            amount: paymentData.amount ? `₩ ${paymentData.amount.toLocaleString()}` : '미상',
            refundAmount: refundData.refund_amount ? `₩ ${refundData.refund_amount.toLocaleString()}` : 
                         (refundInfo.refund_amount ? `₩ ${refundInfo.refund_amount.toLocaleString()}` : '미상'),
            refundReason: '환불 사유',
            refundDetail: refundData.reason || '상세 사유 없음',
            status: refundData.status || 'pending',
            cardInfo: cardInfo.company && cardInfo.number ? 
                     `${cardInfo.company} ${cardInfo.number} (${cardInfo.type || '카드'})` : 
                     (paymentData.payment_method || '결제 정보 없음'),
            requestDate: requestDate,
            statusKorean: statusKorean
          };
          
          console.log('포맷팅된 환불 요청 데이터:', formattedRefundData);
          setRefundData(formattedRefundData);
        } else {
          setError('환불 요청 정보를 불러올 수 없습니다.');
          console.error('환불 요청 정보 로드 실패:', response);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('환불 요청 상세 정보 로딩 오류:', err);
        setError('환불 요청 정보를 불러오는 중 오류가 발생했습니다: ' + err.message);
        setLoading(false);
      }
    };

    if (id) {
      fetchRefundData();
    } else {
      setError('유효한 환불 요청 ID가 없습니다.');
      setLoading(false);
    }
  }, [id]);

  const handleCheckPolicy = () => {
    // 환불 규정 페이지로 이동 - 새 창 대신 같은 페이지에서 이동
    navigate('/admin/refund-policy');
  };

  const handleApproveRefund = () => {
    // 환불 승인 확인 다이얼로그 표시
    setDialogType(Dialog.TYPES.REFUND_APPROVE_CONFIRM);
    setDialogOpen(true);
  };

  const handleConfirmApproval = async () => {
    // 다이얼로그 닫기
    setDialogOpen(false);
    
    try {
      // 환불 요청 승인 API 호출
      console.log('환불 요청 승인 시작 - 환불 ID:', id);
      
      // 환불 금액 계산 - 문자열에서 숫자로 변환
      let refundAmount = 0;
      if (refundData && refundData.refundAmount) {
        // "₩ 50,000" 형식에서 숫자만 추출
        const amountStr = refundData.refundAmount.replace(/[^\d]/g, '');
        refundAmount = parseInt(amountStr, 10) || 0;
      }
      
      console.log('환불 승인 금액:', refundAmount);
      
      const response = await approveRefundRequest(
        id, 
        refundData.refundDetail || "환불 요청 승인", 
        refundAmount
      );
      
      console.log('환불 요청 승인 응답:', response);
      
      // 환불 승인 완료 다이얼로그 표시
      setTimeout(() => {
        setDialogType(Dialog.TYPES.REFUND_APPROVE_COMPLETE);
        setDialogOpen(true);
      }, 300);
    } catch (error) {
      console.error('환불 요청 승인 오류:', error);
      alert(`환불 요청 승인 중 오류가 발생했습니다: ${error.message}`);
    }
  };

  const handleCompleteApproval = () => {
    // 환불 승인 완료 후 지원 페이지로 이동
    setDialogOpen(false);
    navigate('/admin/support');
  };

  const handleRejectRefund = () => {
    // 환불 거절 확인 다이얼로그 표시
    setDialogType(Dialog.TYPES.REFUND_REJECT_CONFIRM);
    setDialogOpen(true);
  };

  const handleConfirmReject = async () => {
    // 다이얼로그 닫기
    setDialogOpen(false);
    
    try {
      // 환불 요청 거절 API 호출
      console.log('환불 요청 거절 시작 - 환불 ID:', id);
      const response = await rejectRefundRequest(
        id,
        "관리자에 의한 환불 거절"
      );
      
      console.log('환불 요청 거절 응답:', response);
      
      // 환불 거절 완료 다이얼로그 표시
      setTimeout(() => {
        setDialogType(Dialog.TYPES.REFUND_REJECT_COMPLETE);
        setDialogOpen(true);
      }, 300);
    } catch (error) {
      console.error('환불 요청 거절 오류:', error);
      alert(`환불 요청 거절 중 오류가 발생했습니다: ${error.message}`);
    }
  };

  const handleCompleteReject = () => {
    // 환불 거절 완료 후 지원 페이지로 이동
    setDialogOpen(false);
    navigate('/admin/support');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleDialogConfirm = () => {
    if (dialogType === Dialog.TYPES.REFUND_APPROVE_CONFIRM) {
      handleConfirmApproval();
    } else if (dialogType === Dialog.TYPES.REFUND_APPROVE_COMPLETE) {
      handleCompleteApproval();
    } else if (dialogType === Dialog.TYPES.REFUND_REJECT_CONFIRM) {
      handleConfirmReject();
    } else if (dialogType === Dialog.TYPES.REFUND_REJECT_COMPLETE) {
      handleCompleteReject();
    }
  };

  if (loading) return <div className="loading">로딩 중...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!refundData) return <div className="error">환불 요청 정보를 찾을 수 없습니다.</div>;

  return (
    <div className="refund-detail-container">
      <h1 className="refund-detail-title">환불 요청 승인/거절</h1>
      
      <div className="refund-section">
        <h2 className="refund-section-title">환불 요청 업체</h2>
        <div className="refund-info-box">
          <div className="refund-info-item">
            <span className="refund-info-label">업체명 :</span>
            <span className="refund-info-value">{refundData.companyName}</span>
          </div>
          <div className="refund-info-item">
            <span className="refund-info-label">전화번호 :</span>
            <span className="refund-info-value">{refundData.phone}</span>
          </div>
          <div className="refund-info-item">
            <span className="refund-info-label">회사 코드 :</span>
            <span className="refund-info-value">{refundData.companyCode}</span>
          </div>
        </div>
      </div>
      
      <div className="refund-section">
        <h2 className="refund-section-title">환불 대상</h2>
        <div className="refund-info-box">
          <div className="refund-info-item">
            <span className="refund-info-label">거래 ID :</span>
            <span className="refund-info-value">{refundData.transactionId}</span>
          </div>
          <div className="refund-info-item">
            <span className="refund-info-label">결제방법 :</span>
            <span className="refund-info-value">{refundData.cardInfo || refundData.paymentMethod}</span>
          </div>
          <div className="refund-info-item">
            <span className="refund-info-label">구매 일자 :</span>
            <span className="refund-info-value">{refundData.purchaseDate}</span>
          </div>
          <div className="refund-info-item">
            <span className="refund-info-label">플랜 :</span>
            <span className="refund-info-value">{refundData.plan}</span>
          </div>
          <div className="refund-info-item">
            <span className="refund-info-label">플랜 적용 기간 :</span>
            <span className="refund-info-value">{refundData.planPeriod}</span>
          </div>
          <div className="refund-info-item">
            <span className="refund-info-label">환불 금액 :</span>
            <span className="refund-info-value">{refundData.refundAmount}</span>
          </div>
        </div>
      </div>
      
      <div className="refund-section">
        <h2 className="refund-section-title">환불 사유</h2>
        <div className="refund-reason-type">{refundData.refundReason}</div>
        <div className="refund-reason-detail">{refundData.refundDetail}</div>
      </div>
      
      <div className="refund-actions">
        <button className="check-policy-button" onClick={handleCheckPolicy}>
          <span>환불 규정 확인하기</span>
          <span className="arrow-icon">&gt;</span>
        </button>
        
        {refundData.status === 'requested' && (
          <div className="refund-decision-buttons">
            <button className="approve-button" onClick={handleApproveRefund}>
              환불 요청 승인
            </button>
            <button className="reject-button" onClick={handleRejectRefund}>
              환불 요청 거절
            </button>
          </div>
        )}
        
        {refundData.status !== 'requested' && (
          <div className="refund-status-message">
            이미 처리된 환불 요청입니다. (상태: {refundData.statusKorean})
          </div>
        )}
      </div>

      <Dialog 
        isOpen={dialogOpen} 
        type={dialogType} 
        onClose={handleCloseDialog} 
        onConfirm={handleDialogConfirm} 
      />
    </div>
  );
};

export default AdminRefundDetail; 